import { useEffect } from 'react'
import { SidebarTrigger } from '@/components/ui/sidebar'

type PageHeaderProps = {
  title: string
  /** One line of context under the title, such as the week or the number of items. */
  description?: React.ReactNode
  /** Browser tab title; defaults to the page title. */
  documentTitle?: string
  actions?: React.ReactNode
  children?: React.ReactNode
}

export function PageHeader({
  title,
  description,
  documentTitle,
  actions,
  children
}: PageHeaderProps): React.JSX.Element {
  const tabTitle = documentTitle ?? title
  useEffect(() => {
    document.title = `${tabTitle} · c-sheet`
  }, [tabTitle])

  return (
    <header className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {/* On wider screens the toggle lives in the sidebar itself. */}
          <SidebarTrigger aria-label="Open navigation" className="md:hidden" />
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions && <div className="ml-auto flex items-center gap-5">{actions}</div>}
      </div>
      {children}
    </header>
  )
}

/** Page frame shared by every screen: wide for tables, narrow for forms. */
export function PageBody({
  width = 'wide',
  children
}: {
  width?: 'wide' | 'narrow'
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <main className="min-h-full p-4 sm:p-6">
      <section
        className={`mx-auto flex w-full flex-col gap-6 ${width === 'wide' ? 'max-w-7xl' : 'max-w-3xl'}`}
      >
        {children}
      </section>
    </main>
  )
}
